import { useCallback } from 'react'

import * as anchor from '@coral-xyz/anchor'
import { TOKEN_PROGRAM_ID, createApproveInstruction, getAssociatedTokenAddress } from '@solana/spl-token'
import { useWallet } from '@solana/wallet-adapter-react'
import { ComputeBudgetProgram, PublicKey, Transaction } from '@solana/web3.js'

import { ORDER_BOOK_PROGRAM_ID, getHighPriorityConnection } from '@src/chains/solana/anchor/setup'
import { findProgramAddress } from '@src/chains/solana/utils'
import { confirmTransactionWithRetry, sendTransactionWithRetry } from '@src/chains/solana/utils/retryConnection'
import { MAX_COMPUTE_UNITS } from '@src/constants/exchange'
import { createContextLogger } from '@src/utils/logger'

const solanaLogger = createContextLogger('Solana')

const useApproveDelegate = () => {
    const { signTransaction } = useWallet()

    /**
     * Constructs the approve delegate transaction and converts it into a versioned transaction.
     *
     * @param tokenMint - The mint address of the token.
     * @param ownerPublicKey - The public key of the owner (or dummy key for simulation).
     * @returns An object containing:
     *    - finalTx: The constructed Transaction.
     *    - blockHash: The recent blockhash used.
     *    - versionedTransaction: The corresponding VersionedTransaction.
     */
    const getApproveDelegateData = async (
        tokenMint: PublicKey,
        ownerPublicKey: PublicKey
    ): Promise<{
        finalTx: Transaction
        blockHash: string
        versionedTransaction: anchor.web3.VersionedTransaction
    }> => {
        // Use high priority connection for better reliability
        const highPriorityConnection = getHighPriorityConnection()

        // Get the associated token account for this owner and mint
        const tokenAccount = await getAssociatedTokenAddress(tokenMint, ownerPublicKey)

        // Get the oapp PDA - using "TristeroOapp" as seed
        const { publicKey: oappPda } = findProgramAddress('TristeroOapp', ORDER_BOOK_PROGRAM_ID)

        // Log verification details
        // solanaLogger.info('Verifying Account Details:', {
        //     programId: ORDER_BOOK_PROGRAM_ID.toString(),
        //     oappPda: oappPda.toString(),
        //     tokenMint: tokenMint.toString(),
        //     tokenAccount: tokenAccount.toString(),
        //     owner: ownerPublicKey.toString(),
        // })

        // Get token account info before approval
        // const tokenAccountInfo = await getAccount(highPriorityConnection, tokenAccount)
        // solanaLogger.info('Token Account Details before approval:', {
        //     owner: tokenAccountInfo.owner.toString(),
        //     mint: tokenAccountInfo.mint.toString(),
        //     amount: tokenAccountInfo.amount.toString(),
        //     delegate: tokenAccountInfo.delegate?.toString(),
        //     delegatedAmount: tokenAccountInfo.delegatedAmount.toString(),
        // })

        // Add compute budget instructions
        const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
            units: 65_000,
        })
        const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: 7_500_000,
        })

        // Create approve instruction with maximum amount (2^64 - 1)
        const approveIx = createApproveInstruction(
            tokenAccount, // Token account
            oappPda, // Delegate (using oappPda)
            ownerPublicKey, // Owner of the token account
            MAX_COMPUTE_UNITS, // Maximum possible amount
            [], // No additional signers needed
            TOKEN_PROGRAM_ID
        )

        // Get recent blockhash
        const { blockhash } = await highPriorityConnection.getLatestBlockhash('confirmed')

        // Create and populate transaction
        const finalTx = new Transaction()
        finalTx.add(modifyComputeUnits).add(addPriorityFee).add(approveIx)
        finalTx.recentBlockhash = blockhash
        finalTx.feePayer = ownerPublicKey

        // Convert the transaction into a versioned transaction.
        const transactionMessageV0 = new anchor.web3.TransactionMessage({
            payerKey: ownerPublicKey,
            recentBlockhash: blockhash,
            instructions: finalTx.instructions,
        }).compileToV0Message()
        const versionedTransaction = new anchor.web3.VersionedTransaction(transactionMessageV0)

        return { finalTx, blockHash: blockhash, versionedTransaction }
    }

    /**
     * Approves the delegate by signing and sending the constructed transaction.
     *
     * @param tokenMint - The mint address of the token.
     * @param owner - The public key of the owner.
     * @returns The transaction signature.
     */
    const approveDelegate = useCallback(
        async (tokenMint: PublicKey, owner: PublicKey): Promise<string> => {
            if (!signTransaction) throw new Error('Wallet does not support signing')
            try {
                // We don't need to store the connection since we're using the retry utility

                // Construct the approval transaction data
                const { finalTx } = await getApproveDelegateData(tokenMint, owner)

                // Sign the transaction
                const signedTx = await signTransaction(finalTx)

                // Send transaction with retry mechanism and return the signature
                const signature = await sendTransactionWithRetry(signedTx, {
                    skipPreflight: true,
                    preflightCommitment: 'processed',
                    maxRetries: 5,
                })

                // solanaLogger.info('Approval transaction sent', { signature })
                return signature
            } catch (error) {
                solanaLogger.error('Error approving delegate:', { error })
                throw error
            }
        },
        [getApproveDelegateData, signTransaction]
    )

    /**
     * Waits for the approval transaction to be confirmed.
     *
     * @param signature - The transaction signature.
     */
    const waitForApproval = useCallback(async (signature: string): Promise<void> => {
        try {
            await confirmTransactionWithRetry(signature, {
                preflightCommitment: 'processed',
                maxRetries: 5,
            })
        } catch (error) {
            solanaLogger.error('Error waiting for approval:', { error })
            throw error
        }
    }, [])

    return { getApproveDelegateData, approveDelegate, waitForApproval }
}

export default useApproveDelegate
